import React, { useState } from "react";
import { useDAWStore } from "../../store/dawStore";
import Knob from "./Knob";
import { Plus, Minus, FolderOpen } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../../auth/AuthContext";
import ProducerPaywallModal from "../ProducerPaywallModal";

// v18.1.1 — CrowLogo blob removed; inline <img> uses the official
// standing-crow asset.
const CROW_LOGO_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/wingman/474b1af8-cac1-436b-9e48-46e676be766d/attachments/050a3ab1b91e401aaab5c54a36a3415b_photo.jpg";

export default function StartupModal() {
  const { initProject, loadProject } = useDAWStore();
  // v19.0 — First Beat Free paywall. `firstProjectId` is set on the user
  // record the moment they save their trial project; every subsequent
  // "Create Project" click is gated behind the Producer Package.
  const { hasProducerAccess, firstProjectId } = useAuth();
  const [showPaywall, setShowPaywall] = useState(false);
  const [bpm, setBpm] = useState(120);
  // v14.0.1 — Local string buffer for the BPM input so the field can be
  // freely cleared and re-typed. Committed (clamp 40-300 + push to
  // parent bpm state) on blur or Enter, kept in sync with the knob via
  // the useEffect below.
  const [bpmText, setBpmText] = useState("120");
  React.useEffect(() => { setBpmText(String(bpm)); }, [bpm]);
  const commitBpm = () => {
    const parsed = parseInt(bpmText, 10);
    const clamped = Number.isFinite(parsed) ? Math.max(40, Math.min(300, parsed)) : bpm;
    setBpmText(String(clamped));
    setBpm(clamped);
  };
  const [trackCount, setTrackCount] = useState(4);
  const [projectName, setProjectName] = useState("Untitled Project");

  const handleStart = () => {
    // v19.0 — Trial-beat paywall gate. Any user who has already claimed
    // their free-beat slot (`firstProjectId`) and doesn't hold an active
    // Producer subscription is blocked here — the Stripe upgrade modal
    // opens instead of a fresh session.
    if (firstProjectId && !hasProducerAccess) {
      setShowPaywall(true);
      return;
    }
    initProject({ bpm, trackCount, projectName });
  };

  const handleLoad = () => {
    const ok = loadProject();
    if (!ok) toast.error("No saved project found");
    else toast.success("Project loaded");
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/80 backdrop-blur-sm" data-testid="startup-modal">
      <div
        className="plugin-panel p-8 w-full max-w-md mx-4"
        style={{ background: "linear-gradient(180deg, #1a1a1a, #0f0f0f)" }}
      >
        <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#262626]">
          <img src={CROW_LOGO_URL} alt="CROW" style={{ width: 32, height: 32, objectFit: "contain", borderRadius: 5 }} />
          <div>
            <h1 className="font-display font-extrabold text-xl tracking-tight">New Project</h1>
            {/* v19.2 — Cleaned branding to match TopBar. No dots, no tagline. */}
            <div className="label-mono">CROW</div>
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <label className="label-mono mb-2 block">Project Name</label>
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              className="w-full bg-[#0a0a0a] border border-[#222] rounded-md px-3 py-2.5 text-sm font-mono focus:outline-none focus:border-[#444]"
              data-testid="startup-project-name"
            />
          </div>

          <div className="grid grid-cols-2 gap-5">
            <div>
              <label className="label-mono mb-3 block text-center">Tempo (BPM)</label>
              <div className="flex flex-col items-center gap-2">
                <Knob
                  value={bpm}
                  min={40} max={300} step={1}
                  size={72}
                  accentColor="#00FF41"
                  onChange={(v) => setBpm(Math.round(v))}
                  format={(v) => `${Math.round(v)}`}
                  data-testid="startup-bpm-knob"
                />
                {/* v14.0.1 — Freely-typeable BPM input: local string
                    state so clearing "40" to type "150" works. */}
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={bpmText}
                  onChange={(e) => setBpmText(e.target.value.replace(/[^0-9]/g, ""))}
                  onBlur={commitBpm}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.currentTarget.blur(); }
                    else if (e.key === "Escape") { setBpmText(String(bpm)); e.currentTarget.blur(); }
                  }}
                  className="time-display px-3 py-1 text-base bg-[#111] border border-[#262626] rounded w-24 text-center focus:outline-none focus:border-[#666]"
                  data-testid="startup-bpm-input"
                />
              </div>
            </div>

            <div>
              <label className="label-mono mb-3 block text-center">Tracks</label>
              <div className="flex flex-col items-center gap-2 pt-2">
                <div className="flex items-center gap-3">
                  <button
                    className="btn-3d w-8 h-8 flex items-center justify-center"
                    onClick={() => setTrackCount((n) => Math.max(1, n - 1))}
                    data-testid="startup-tracks-minus"
                  ><Minus size={14} weight="bold" /></button>
                  <div
                    className="time-display px-4 py-2 text-2xl tracking-wider min-w-[60px] text-center"
                    data-testid="startup-tracks-value"
                  >{trackCount}</div>
                  <button
                    className="btn-3d w-8 h-8 flex items-center justify-center"
                    onClick={() => setTrackCount((n) => Math.min(16, n + 1))}
                    data-testid="startup-tracks-plus"
                  ><Plus size={14} weight="bold" /></button>
                </div>
                <div className="label-mono mt-1">empty tracks · drag instruments later</div>
              </div>
            </div>
          </div>

          <div className="flex gap-2 pt-4 border-t border-[#262626]">
            <button
              className="btn-3d flex-1 py-3 text-sm font-semibold flex items-center justify-center gap-2"
              onClick={handleLoad}
              data-testid="startup-load-btn"
            >
              <FolderOpen size={14} weight="bold" />
              Load Saved
            </button>
            <button
              className="btn-3d-accent btn-3d flex-1 py-3 text-sm font-bold"
              onClick={handleStart}
              data-testid="startup-create-btn"
            >
              Create Project
            </button>
          </div>
        </div>
      </div>
      {/* v19.0 — Trial-beat paywall. Shown when the user has already saved
          a first beat and tries to create another without Producer access. */}
      <ProducerPaywallModal
        open={showPaywall}
        onClose={() => setShowPaywall(false)}
        headline="You've already used your free beat."
        subline="Upgrade to the Producer Package to start unlimited new projects."
      />
    </div>
  );
}
